import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { useCountryStore } from '@/store/countryStore';
import { getOptimizedStorageImage } from '@/lib/imageOptimization';

interface CaseType {
  id: string;
  name: string;
  brand: string | null;
  image_url: string | null;
  price: number | null;
}

interface CaseCardProps {
  caseType: CaseType;
  index?: number;
}

const CaseCard = forwardRef<HTMLAnchorElement, CaseCardProps>(({ caseType }, ref) => {
  const { getAdjustedPrice, formatPrice } = useCountryStore();
  const originalPrice = caseType.price ?? 10000;
  const isFree = originalPrice === 0;
  const displayPrice = isFree ? 0 : getAdjustedPrice(originalPrice);
  const image320 = caseType.image_url ? getOptimizedStorageImage(caseType.image_url, 320, 62) : '';
  const image480 = caseType.image_url ? getOptimizedStorageImage(caseType.image_url, 480, 65) : '';
  
  return (
    <Link 
      ref={ref}
      to={`/case/${caseType.id}`}
      className="block"
    >
      <div className="overflow-hidden">
        <div className="relative aspect-[3/4] overflow-hidden rounded-2xl mb-2 bg-muted">
          {caseType.image_url ? (
            <img
              src={image480}
              srcSet={`${image320} 320w, ${image480} 480w`}
              sizes="(max-width: 639px) 48vw, (max-width: 1023px) 31vw, 23vw"
              alt={caseType.name}
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              width="480"
              height="640"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-muted-foreground text-4xl">📱</span>
            </div>
          )}
          {isFree && (
            <div className="absolute top-2 right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              مجاني
            </div>
          )}
        </div>
        
        <div className="space-y-1 px-1">
          <h3 className="font-medium text-sm text-foreground line-clamp-1">
            {caseType.name}
          </h3>
          <p className={`text-sm font-bold ${isFree ? 'text-green-500' : 'text-foreground'}`}>
            {isFree ? 'مجاني' : formatPrice(displayPrice)}
          </p>
        </div>
      </div>
    </Link>
  );
});

CaseCard.displayName = 'CaseCard';

export default CaseCard;
